<template>
  <section>
    <div class="wardflexCalculator">
        <div class="wardflexCalculator__grid">
        <!-- Form Section (First Row) -->
        <div class="wardflexCalculator__formSection">
            <div class="wardflexCalculator__column form">
            <p class="intro">
                Configure your gas piping specifications below to generate a capacity table.
                <span>All fields are required.</span>
            </p>

            <div class="wardflexCalculator__form">
                <!-- Gas Type Selection -->
                <label class="wardflexCalculator__label">Gas Type</label>
                <div class="wardflexCalculator__select">
                <label class="selectWrap">
                    <select
                    class="wardflexCalculator__input"
                    :value="gasType"
                    @change="e => setGasType(e.target.value)"
                    >
                    <option value="" disabled>Select gas type...</option>
                    <option value="NaturalGas">Natural Gas</option>
                    <option value="Propane">Propane (LPG)</option>
                    </select>
                </label>
                </div>

                <!-- Gas Supply Pressure -->
                <label class="wardflexCalculator__label">Gas Supply Pressure</label>
                <div class="wardflexCalculator__inputGroup">
                <input
                    type="number"
                    class="wardflexCalculator__input"
                    :value="gasPressure"
                    @input="e => setGasPressure(parseFloat(e.target.value) || 0)"
                    placeholder="Enter pressure"
                    step="0.1"
                    min="0"
                />
                <label class="selectWrap">
                    <select
                    class="wardflexCalculator__input"
                    :value="gasPressureUnit"
                    @change="e => setGasPressureUnit(e.target.value)"
                    >
                    <option value="psi">PSI</option>
                    <option value="inchesWC">Inches W.C.</option>
                    </select>
                </label>
                </div>

                <!-- Pressure Drop -->
                <label class="wardflexCalculator__label">Allowable Pressure Drop</label>
                <div class="wardflexCalculator__inputGroup">
                <input
                    type="number"
                    class="wardflexCalculator__input"
                    :value="pressureDrop"
                    @input="e => setPressureDrop(parseFloat(e.target.value) || 0)"
                    placeholder="Enter pressure drop"
                    step="0.1"
                    min="0"
                />
                <label class="selectWrap">
                    <select
                    class="wardflexCalculator__input"
                    :value="pressureDropUnit"
                    @change="e => setPressureDropUnit(e.target.value)"
                    >
                    <option value="psi">PSI</option>
                    <option value="inchesWC">Inches W.C.</option>
                    </select>
                </label>
                </div>

                <!-- Length Range -->
                <label class="wardflexCalculator__label">Minimum Length (feet)</label>
                <input
                type="number"
                class="wardflexCalculator__input"
                :value="minLength"
                @input="e => setMinLength(parseInt(e.target.value) || 0)"
                placeholder="Enter minimum length"
                min="1"
                />

                <label class="wardflexCalculator__label">Maximum Length (feet)</label>
                <input
                type="number"
                class="wardflexCalculator__input"
                :value="maxLength"
                @input="e => setMaxLength(parseInt(e.target.value) || 0)"
                placeholder="Enter maximum length"
                min="1"
                max="2000"
                />

                <label class="wardflexCalculator__label">Length Increments (feet)</label>
                <input
                type="number"
                class="wardflexCalculator__input"
                :value="lengthIncrements"
                @input="e => setLengthIncrements(parseInt(e.target.value) || 0)"
                placeholder="Enter increment"
                min="1"
                />

                <!-- Calculate Button -->
                <button
                class="wardflexCalculator__button"
                @click="calculateTable"
                :disabled="!allFieldsFilled"
                >
                Generate Capacity Table
                </button>

                <!-- Error Display -->
                <div v-if="error" class="wardflexCalculator__error">
                {{ error }}
                </div>
            </div>
            </div>
        </div>

        <!-- Results Section (Second Row) -->
        <div class="wardflexCalculator__resultsSection">
            <!-- Default state -->
            <div v-if="!hasResults && !error" class="wardflexCalculator__null">
            <p class="intro">
                Complete the form above to generate your WARDFlex® capacity table.
                The table will show maximum flow rates for different tubing sizes and lengths.
            </p>
            </div>

            <!-- Results Display -->
            <div v-if="hasResults" class="wardflexCalculator__results">
            <h2 class="-brandBlue">Capacity Table Results</h2>

            <!-- Header Text -->
            <div class="wardflexCalculator__resultHeader">
                <p>{{ resultData.metadata.headerText }}</p>
            </div>

            <!-- Table Display -->
            <div class="wardflexCalculator__tableContainer">
                <table class="wardflexCalculator__table">
                <thead>
                    <tr>
                    <th v-for="(header, index) in resultData.data[0]" :key="index">
                        {{ header }}
                    </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, rowIndex) in resultData.data.slice(1)" :key="rowIndex">
                    <td v-for="(cell, cellIndex) in row" :key="cellIndex" :class="cellIndex === 0 ? 'length-size' : 'capacity'">
                        {{ cell }}
                    </td>
                    </tr>
                </tbody>
                </table>
            </div>

            <!-- Footer Text -->
            <div class="wardflexCalculator__resultFooter">
                <p><small>{{ resultData.metadata.footerText }}</small></p>
            </div>

            <!-- Download Options -->
            <div class="wardflexCalculator__actions">
                <button class="wardflexCalculator__button secondary" @click="printTable">
                Print Table
                </button>
                <button class="wardflexCalculator__button secondary" @click="exportCSV">
                Export as CSV
                </button>
            </div>
            </div>
        </div>
        </div>

        <footer class="wardflexCalculator__footer">
        <span>Need additional assistance?</span>
        <p>You can reach our team by emailing us via the link below or calling 303-715-9990.</p>
        <p class="button"><a href="mailto:sales@asc-es.com">Contact ASC</a></p>
        </footer>
    </div>
  </section>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex'

export default {
  name: 'WardflexCalculator',

  computed: {
    ...mapState('wardflexCalculator', [
      'gasType',
      'gasPressure',
      'gasPressureUnit',
      'pressureDrop',
      'pressureDropUnit',
      'minLength',
      'maxLength',
      'lengthIncrements',
      'error',
      'resultData'
    ]),

    ...mapGetters('wardflexCalculator', [
      'allFieldsFilled',
      'hasResults'
    ])
  },

  methods: {
    ...mapMutations('wardflexCalculator', [
      'setGasType',
      'setGasPressure',
      'setGasPressureUnit',
      'setPressureDrop',
      'setPressureDropUnit',
      'setMinLength',
      'setMaxLength',
      'setLengthIncrements',
      'setError',
      'setResultData'
    ]),

    ...mapActions('wardflexCalculator', [
      'calculateTable'
    ]),

    printTable() {
      window.print();
    },

    exportCSV() {
      if (!this.hasResults) return;

      const csvContent = this.resultData.data.map(row =>
        row.map(cell => `"${cell}"`).join(',')
      ).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wardflex-capacity-table-${this.gasType}-${Date.now()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  }
}
</script>

